import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useT } from "../i18n";
import ExportButtons from "../components/ExportButtons";

/** Comparador de precios (feature ULTRA). Desde el 20 oct 2026 (feedback de Carlos)
 *  la evolución es POR AGRUPACIÓN HOMOGÉNEA y sale del histórico de precios
 *  (app/precios.py), con el PVP con IVA y el PVL separados: antes se mezclaban dosis,
 *  combinaciones y dos precios distintos bajo la etiqueta «PVL». */

interface PriceProduct {
  cn_code: string; product_name: string; active_ingredient: string | null;
  ah_code: string | null; ah_name: string | null; has_ah: boolean; price_months: number;
}
interface Point { month: string; value: number }
interface Presentation {
  cn_code: string; product_name: string; laboratory: string | null;
  is_own: boolean; is_tracked: boolean; en_agrupacion: boolean; comercializado?: boolean | null;
  series: Point[]; latest: number | null; latest_month: string | null; points: number;
  diff_pct_vs_own: number | null;
}
interface Evolution {
  ah: { code: string; name: string | null } | null; metric: Metric; focus_cn: string | null;
  ref_cn?: string | null; sin_agrupacion: boolean; meses: string[]; precio_menor: Point[];
  presentaciones: Presentation[]; omitted: number;
}
interface AhOption { code: string; name: string | null; presentaciones: number; own: number; tracked: number }
type Metric = "pvp_iva" | "pvl";
type Period = "1" | "3" | "5" | "all";

// Comparador homogéneo (R-27…R-29, §90 Fase 4A)
type Level = "presentation" | "unit" | "product";
interface HRow {
  cn_code: string; product_name: string | null; laboratory: string | null; ah_code: string; ah_name: string | null;
  pvp: number | null; pvl: number | null; precio_menor: number | null;
  units: number | null; unit_word: string | null; pvp_per_unit: number | null; pvl_per_unit: number | null;
  same_pack?: boolean;
  is_own: boolean; is_tracked: boolean; at_lowest: boolean;
  diff_pct_vs_own: number | null; diff_pct_vs_lowest: number | null; reason?: string;
}
interface HProduct {
  brand: string; laboratory: string | null; presentations: number;
  avg_pvp_per_unit: number | null; min_pvp_per_unit: number | null; max_pvp_per_unit: number | null;
  is_own: boolean; is_tracked: boolean; diff_pct_vs_own: number | null;
}
interface Homogeneous {
  cn: string; level: Level; metric: "pvp" | "pvp_per_unit";
  ah: { code: string; name: string | null; precio_menor: number | null; members: number; updated_at: string | null };
  dose_key: string | null; own: HRow | null; rows: HRow[]; excluded: HRow[]; products: HProduct[] | null;
}

const eur = (v: number | null | undefined, digits = 2) => v == null ? "—" : `${v.toFixed(digits)} €`;

function Diff({ v }: { v: number | null }) {
  if (v == null) return <span style={{ color: "var(--clr-text4)" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: 3, background: v < 0 ? "#22c55e" : v > 0 ? "#ef4444" : "#94a3b8", display: "inline-block" }} />
      {`${v > 0 ? "+" : ""}${v}%`}
    </span>
  );
}

function HomogeneousBlock({ cn, card, isMobile, G }: { cn: string; card: React.CSSProperties; isMobile: boolean; G: string }) {
  const t = useT();
  const [level, setLevel] = useState<Level>("presentation");      // R-28: misma AH por defecto
  const [data, setData] = useState<Homogeneous | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!cn) return;
    setLoading(true); setErr(null);
    api.get<Homogeneous>(`/prices/homogeneous?cn=${encodeURIComponent(cn)}&level=${level}`)
      .then(setData)
      .catch((e: any) => { setData(null); setErr(e?.message || t("prices.hError")); })
      .finally(() => setLoading(false));
  }, [cn, level]);

  const th: React.CSSProperties = { padding: "0.7rem 10px", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", textAlign: "left", whiteSpace: "nowrap" };
  const td: React.CSSProperties = { padding: "7px 10px", borderTop: "1px solid var(--clr-border2)" };
  const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
  const perUnit = level !== "presentation";

  return (
    <div style={{ ...card, overflow: "hidden", marginBottom: 18 }}>
      <div style={{ padding: "0.9rem 1.1rem 0.6rem", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>{t("prices.hTitle")}</span>
        {data && (
          <span style={{ fontSize: "0.72rem", color: "var(--clr-text3)" }}>
            AH {data.ah.code}{data.ah.name ? ` · ${data.ah.name}` : ""} · {t("prices.hMembers", { n: data.ah.members })} · {t("prices.hLowest")} {eur(data.ah.precio_menor)}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden" }}>
          {(["presentation", "unit", "product"] as Level[]).map(l => (
            <button key={l} onClick={() => setLevel(l)} title={t(`prices.hLevelHint_${l}`)} style={{
              border: 0, cursor: "pointer", padding: "6px 12px", fontSize: "0.75rem", fontWeight: 600,
              background: level === l ? G : "transparent", color: level === l ? "#fff" : "var(--clr-text3)",
            }}>{t(`prices.hLevel_${l}`)}</button>
          ))}
        </div>
      </div>
      <div style={{ padding: "0 1.1rem 0.6rem", fontSize: "0.72rem", color: "var(--clr-text4)" }}>{t(`prices.hLevelHint_${level}`)}</div>

      {loading && <div style={{ padding: "1rem 1.1rem", color: "var(--clr-text4)", fontSize: "0.82rem" }}>{t("prices.loadingComparison")}</div>}
      {err && !loading && (
        <div style={{ margin: "0 1.1rem 0.9rem", padding: "0.7rem 1rem", borderRadius: 12, background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.35)", fontSize: "0.8rem", color: "var(--clr-text2)" }}>
          {err}
        </div>
      )}

      {data && !loading && level !== "product" && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead><tr>
              <th style={th}>{t("prices.colProduct")}</th>
              {!isMobile && <th style={th}>{t("prices.colLaboratory")}</th>}
              {perUnit && <th style={{ ...th, textAlign: "right" }}>{t("prices.hColPack")}</th>}
              <th style={{ ...th, textAlign: "right" }}>PVP</th>
              {!isMobile && <th style={{ ...th, textAlign: "right" }}>PVL</th>}
              {perUnit && <th style={{ ...th, textAlign: "right" }}>{t("prices.hColPerUnit")}</th>}
              <th style={{ ...th, textAlign: "right" }}>{t("prices.hColVsOwn")}</th>
              {!perUnit && <th style={{ ...th, textAlign: "right" }}>{t("prices.hColVsLowest")}</th>}
            </tr></thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.cn_code} style={{ background: r.is_own ? `${G}0d` : undefined, fontWeight: r.is_own ? 600 : 400 }}>
                  <td style={td}>
                    {r.product_name}
                    {r.is_tracked && <span style={{ marginLeft: 6, fontSize: "0.62rem", color: "var(--clr-text3)" }}>· {t("prices.tracked")}</span>}
                    {r.at_lowest && !perUnit && <span style={{ marginLeft: 6, fontSize: "0.62rem", color: "#10b981", fontWeight: 600 }}>· {t("prices.hAtLowest")}</span>}
                  </td>
                  {!isMobile && <td style={{ ...td, color: "var(--clr-text3)" }}>{r.laboratory || "—"}</td>}
                  {perUnit && (
                    <td style={{ ...num, color: "var(--clr-text3)" }}>
                      {r.units != null ? `${r.units} ${r.unit_word}` : "—"}
                      {/* Envase distinto del elegido: la diferencia por unidad
                          mide el tamaño del envase, no una ventaja de precio. */}
                      {!r.is_own && r.same_pack === false && (
                        <span title={t("prices.otherPackHint")}
                          style={{ marginLeft: 6, fontSize: "0.6rem", fontWeight: 600, color: "#b45309",
                                   background: "#f59e0b1e", border: "1px solid #f59e0b33",
                                   borderRadius: 999, padding: "1px 6px", whiteSpace: "nowrap" }}>
                          {t("prices.otherPack")}
                        </span>
                      )}
                    </td>
                  )}
                  <td style={num}>{eur(r.pvp)}</td>
                  {!isMobile && <td style={{ ...num, color: "var(--clr-text3)" }}>{eur(r.pvl)}</td>}
                  {perUnit && <td style={{ ...num, fontWeight: 600 }}>{eur(r.pvp_per_unit, 4)}</td>}
                  <td style={num} title={perUnit && r.same_pack === false ? t("prices.otherPackHint") : undefined}>
                    {r.is_own ? <span style={{ color: "var(--clr-text4)" }}>★</span>
                      : <span style={{ opacity: perUnit && r.same_pack === false ? 0.45 : 1 }}>
                          <Diff v={r.diff_pct_vs_own} />
                        </span>}
                  </td>
                  {!perUnit && <td style={num}><Diff v={r.diff_pct_vs_lowest} /></td>}
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={8} style={{ padding: "1.2rem", textAlign: "center", color: "var(--clr-text4)" }}>{t("prices.noCompetitors")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {data && !loading && level === "product" && data.products && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead><tr>
              <th style={th}>{t("prices.hColBrand")}</th>
              {!isMobile && <th style={th}>{t("prices.colLaboratory")}</th>}
              <th style={{ ...th, textAlign: "right" }}>{t("prices.hColPresentations")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("prices.hColAvgPerUnit")}</th>
              {!isMobile && <th style={{ ...th, textAlign: "right" }}>{t("prices.hColRange")}</th>}
              <th style={{ ...th, textAlign: "right" }}>{t("prices.hColVsOwn")}</th>
            </tr></thead>
            <tbody>
              {data.products.map(p => (
                <tr key={p.brand} style={{ background: p.is_own ? `${G}0d` : undefined, fontWeight: p.is_own ? 600 : 400 }}>
                  <td style={td}>{p.brand}{p.is_tracked && <span style={{ marginLeft: 6, fontSize: "0.62rem", color: "var(--clr-text3)" }}>· {t("prices.tracked")}</span>}</td>
                  {!isMobile && <td style={{ ...td, color: "var(--clr-text3)" }}>{p.laboratory || "—"}</td>}
                  <td style={num}>{p.presentations}</td>
                  <td style={{ ...num, fontWeight: 600 }}>{eur(p.avg_pvp_per_unit, 4)}</td>
                  {!isMobile && <td style={{ ...num, color: "var(--clr-text3)" }}>{p.min_pvp_per_unit != null ? `${eur(p.min_pvp_per_unit, 4)} – ${eur(p.max_pvp_per_unit, 4)}` : "—"}</td>}
                  <td style={num}>{p.is_own ? <span style={{ color: "var(--clr-text4)" }}>★</span> : <Diff v={p.diff_pct_vs_own} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && !loading && data.excluded.length > 0 && (
        /* R-29: lo que no se puede normalizar no se compara; se dice cuál y por qué */
        <div style={{ margin: "0.6rem 1.1rem 0.4rem", padding: "0.6rem 0.9rem", borderRadius: 12, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.3)", fontSize: "0.74rem", color: "var(--clr-text2)" }}>
          <strong>{t("prices.hExcludedTitle", { n: data.excluded.length })}</strong>{" "}
          {data.excluded.map(e => `${e.product_name} (${t(`prices.hReason_${e.reason}`)})`).join(" · ")}
        </div>
      )}
      <div style={{ padding: "0.5rem 1.1rem 0.8rem", fontSize: "0.7rem", color: "var(--clr-text4)" }}>
        {t("prices.hScopeNote")}{data?.ah.updated_at ? ` · ${t("prices.hUpdated", { date: data.ah.updated_at.slice(0, 10) })}` : ""}
      </div>
    </div>
  );
}

const PALETTE = ["#f59e0b", "#8b5cf6", "#ef4444", "#3b82f6", "#10b981",
                 "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16",
                 "#a855f7", "#06b6d4", "#eab308", "#f43f5e", "#22c55e"];

const DEFAULT_OTHERS = 6;      // además de lo propio y la competencia

function sinceOf(period: Period): string | null {
  if (period === "all") return null;
  const d = new Date();
  return `${d.getFullYear() - Number(period)}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function PriceComparisonPage() {
  const t = useT();
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const { isMobile } = useBreakpoint();

  const [products, setProducts] = useState<PriceProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [focusCn, setFocusCn] = useState<string>("");          // presentación de referencia
  const [ah, setAh] = useState<string>("");                    // agrupación elegida
  const [filters, setFilters] = useState({ atc: "", pa: "", producto: "", cn: "" });
  const [options, setOptions] = useState<AhOption[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [metric, setMetric] = useState<Metric>("pvp_iva");
  const [period, setPeriod] = useState<Period>("5");
  const [data, setData] = useState<Evolution | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState<Set<string>>(new Set());
  const [reload, setReload] = useState(0);

  useEffect(() => {
    api.get<{ items: PriceProduct[] }>("/prices/products")
      .then(d => {
        setProducts(d.items);
        const first = d.items.find(i => i.has_ah) || d.items[0];
        if (first) { setFocusCn(first.cn_code); setAh(first.ah_code || ""); }
      })
      .catch(() => setError(t("prices.errorProducts")))
      .finally(() => setLoadingProducts(false));
    api.get<{ items: AhOption[]; truncated: boolean }>("/prices/filters")
      .then(d => { setOptions(d.items); setTruncated(d.truncated); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!ah && !focusCn) return;
    const params = new URLSearchParams({ metric });
    if (ah) params.set("ah", ah);
    if (focusCn) params.set("cn", focusCn);
    const since = sinceOf(period);
    if (since) params.set("desde", since);
    setLoadingData(true); setError(null);
    api.get<Evolution>(`/prices/comparison?${params}`)
      .then(d => {
        setData(d);
        const own = d.presentaciones.filter(p => p.is_own || p.is_tracked || p.cn_code === d.focus_cn);
        const others = d.presentaciones.filter(p => !own.includes(p) && p.en_agrupacion && p.points > 0)
          .slice(0, DEFAULT_OTHERS);
        setVisible(new Set([...own, ...others].map(p => p.cn_code)));
      })
      .catch(() => setError(t("prices.errorComparison")))
      .finally(() => setLoadingData(false));
  }, [ah, focusCn, metric, period, reload]);

  const search = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v.trim()) params.set(k, v.trim()); });
    api.get<{ items: AhOption[]; truncated: boolean }>(`/prices/filters?${params}`)
      .then(d => {
        setOptions(d.items); setTruncated(d.truncated);
        if (d.items.length) { setAh(d.items[0].code); setFocusCn(filters.cn.trim()); }
      })
      .catch(() => setError(t("prices.errorComparison")));
  };
  const clear = () => {
    setFilters({ atc: "", pa: "", producto: "", cn: "" });
    api.get<{ items: AhOption[]; truncated: boolean }>("/prices/filters")
      .then(d => { setOptions(d.items); setTruncated(d.truncated); }).catch(() => {});
  };

  const series = useMemo(() => (data?.presentaciones || []).filter(p => visible.has(p.cn_code) && p.points > 0), [data, visible]);
  const showLowest = metric === "pvp_iva" && !!data && data.precio_menor.length > 0;
  const chartData = useMemo(() => {
    if (!data) return [];
    const months = new Set<string>();
    series.forEach(p => p.series.forEach(s => months.add(s.month)));
    if (showLowest) data.precio_menor.forEach(s => months.add(s.month));
    const byCn = new Map(series.map(p => [p.cn_code, new Map(p.series.map(s => [s.month, s.value]))]));
    const low = new Map(data.precio_menor.map(s => [s.month, s.value]));
    return [...months].sort().map(month => {
      const row: Record<string, string | number | null> = { month };
      series.forEach(p => { row[p.cn_code] = byCn.get(p.cn_code)?.get(month) ?? null; });
      if (showLowest) row.__lowest = low.get(month) ?? null;
      return row;
    });
  }, [data, series, showLowest]);

  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    let i = 0;
    data?.presentaciones.forEach(p => {
      if (p.is_own || p.cn_code === data.ref_cn) map.set(p.cn_code, G);
      else map.set(p.cn_code, PALETTE[i++ % PALETTE.length]);
    });
    return map;
  }, [data, G]);
  const nameOf = (cn: string) => data?.presentaciones.find(p => p.cn_code === cn)?.product_name ?? cn;

  function toggleVisible(cn: string) {
    setVisible(prev => {
      const next = new Set(prev);
      if (next.has(cn)) next.delete(cn); else next.add(cn);
      return next;
    });
  }

  const card: React.CSSProperties = {
    background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
    borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
    boxShadow: "var(--clr-shadow-sm)",
  };
  const field: React.CSSProperties = {
    padding: "8px 12px", borderRadius: 12, fontSize: "0.82rem", minHeight: 38,
    border: "1px solid var(--clr-border)", background: "var(--clr-input-bg)",
    color: "var(--clr-text)", maxWidth: "100%", outline: "none", boxSizing: "border-box",
  };
  const lbl: React.CSSProperties = { fontSize: "0.68rem", fontWeight: 600, color: "var(--clr-text3)", display: "block", marginBottom: 4 };
  const seg = (active: boolean): React.CSSProperties => ({
    border: 0, cursor: "pointer", padding: "6px 12px", fontSize: "0.75rem", fontWeight: 600,
    background: active ? G : "transparent", color: active ? "#fff" : "var(--clr-text3)",
  });
  const homogeneousCn = data?.ref_cn || focusCn || data?.presentaciones.find(p => p.en_agrupacion)?.cn_code || "";
  const fmt = (v: number | null) => v == null ? "—" : `${v.toFixed(2)} €`;

  return (
    <div className="page" style={{ padding: isMobile ? "1rem" : "1.75rem 2rem", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <h1 style={{ margin: "0 0 4px", fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("prices.title")}</h1>
      <p style={{ margin: "0 0 1.25rem", fontSize: "0.82rem", color: "var(--clr-text3)" }}>{t("prices.subtitle")}</p>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.35)", color: "#b91c1c", borderRadius: 12, padding: "0.7rem 1rem", fontSize: "0.83rem", marginBottom: 14 }}>
          {error}{" "}
          <button onClick={() => setReload(n => n + 1)} style={{ background: "none", border: "none", color: "#b91c1c", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>
            {t("prices.retry")}
          </button>
        </div>
      )}

      {/* Selección: producto propio, o multifiltro → agrupación (Carlos, 20 oct) */}
      <div style={{ ...card, padding: "1rem 1.1rem", marginBottom: 16 }}>
        {loadingProducts ? (
          <div style={{ color: "var(--clr-text4)" }}>{t("prices.loadingPortfolio")}</div>
        ) : (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ flex: "1 1 320px", minWidth: 0 }}>
              <span style={lbl}>{t("prices.productLabel")}</span>
              <select value={products.some(p => p.cn_code === focusCn) ? focusCn : ""} style={{ ...field, width: "100%" }}
                onChange={e => { const p = products.find(x => x.cn_code === e.target.value); setFocusCn(e.target.value); setAh(p?.ah_code || ""); }}>
                <option value="">—</option>
                {products.map(p => (
                  <option key={p.cn_code} value={p.cn_code}>
                    {p.product_name} {p.has_ah ? `· AH ${p.ah_code}` : t("prices.noPriceData")}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ flex: "1 1 320px", minWidth: 0 }}>
              <span style={lbl}>{t("prices.ahLabel")}</span>
              <select value={ah} style={{ ...field, width: "100%" }}
                onChange={e => { setAh(e.target.value); setFocusCn(""); }}>
                {!options?.some(o => o.code === ah) && <option value={ah}>{ah ? `${ah}${data?.ah?.name ? ` · ${data.ah.name}` : ""}` : "—"}</option>}
                {(options || []).map(o => (
                  <option key={o.code} value={o.code}>
                    {t("prices.ahOption", { code: o.code, name: o.name || "", n: o.presentaciones })}{o.own ? ` · ${t("prices.ahOwn", { n: o.own })}` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
        <div style={{ marginTop: 12, fontSize: "0.7rem", fontWeight: 700, color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8 }}>{t("prices.filtersTitle")}</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginTop: 6 }}>
          {(["atc", "pa", "producto", "cn"] as const).map(k => {
            const key = k === "atc" ? "Atc" : k === "pa" ? "Pa" : k === "producto" ? "Product" : "Cn";
            return (
              <label key={k} style={{ flex: "1 1 150px", minWidth: 0 }}>
                <span style={lbl}>{t(`prices.filter${key}`)}</span>
                <input value={filters[k]} placeholder={t(`prices.filter${key}Ph`)} style={{ ...field, width: "100%" }}
                  onChange={e => setFilters(f => ({ ...f, [k]: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") search(); }} />
              </label>
            );
          })}
          <button onClick={search} style={{ background: G, color: "#fff", border: 0, borderRadius: 999, padding: "0.55rem 1.2rem", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}>{t("prices.filterSearch")}</button>
          <button onClick={clear} style={{ background: "transparent", color: "var(--clr-text3)", border: "1px solid var(--clr-border)", borderRadius: 999, padding: "0.5rem 1rem", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer" }}>{t("prices.filterClear")}</button>
        </div>
        {options && options.length === 0 && <div style={{ marginTop: 8, fontSize: "0.76rem", color: "var(--clr-text4)" }}>{t("prices.ahNone")}</div>}
        {truncated && <div style={{ marginTop: 8, fontSize: "0.76rem", color: "var(--clr-text4)" }}>{t("prices.ahTruncated")}</div>}
      </div>

      {/* Hoy: comparación homogénea (R-27…R-29) */}
      {homogeneousCn && !data?.sin_agrupacion && <HomogeneousBlock cn={homogeneousCn} card={card} isMobile={isMobile} G={G} />}

      {loadingData && <div style={{ color: "var(--clr-text4)", padding: "1rem 0" }}>{t("prices.loadingComparison")}</div>}

      {data && !loadingData && (
        <>
          <div style={{ ...card, padding: isMobile ? "1rem 0.5rem 0.5rem" : "1.1rem 1.25rem 0.75rem", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", margin: "0 0 8px 8px" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>
                {data.ah ? t("prices.historyTitle", { code: data.ah.code }) : nameOf(data.focus_cn || "")}
                {data.ah?.name && <span style={{ fontWeight: 400, color: "var(--clr-text3)" }}> · {data.ah.name}</span>}
              </span>
              <span style={{ flex: 1 }} />
              {/* Contrato con Italfarmaco, punto 13: la serie que ves, presentación por presentación */}
              <ExportButtons small stem="helixscan_comparador_precios"
                path={`/export/prices?${new URLSearchParams({ metric, ...(ah ? { ah } : {}), ...(focusCn ? { cn: focusCn } : {}),
                                                              ...(sinceOf(period) ? { desde: sinceOf(period)! } : {}) })}`} />
              <div title={t("prices.metricHint")} style={{ display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden" }}>
                <button onClick={() => setMetric("pvp_iva")} style={seg(metric === "pvp_iva")}>{t("prices.metricPvp")}</button>
                <button onClick={() => setMetric("pvl")} style={seg(metric === "pvl")}>{t("prices.metricPvl")}</button>
              </div>
              <div style={{ display: "inline-flex", border: "1px solid var(--clr-border)", borderRadius: 999, overflow: "hidden" }} aria-label={t("prices.periodLabel")}>
                {(["1", "3", "5", "all"] as Period[]).map(p => (
                  <button key={p} onClick={() => setPeriod(p)} style={seg(period === p)}>{t(`prices.period_${p}`)}</button>
                ))}
              </div>
            </div>
            {data.sin_agrupacion && (
              <div style={{ margin: "0 8px 10px", fontSize: "0.76rem", color: "var(--clr-text3)" }}>{t("prices.noAhForProduct")}</div>
            )}
            {chartData.length === 0 ? (
              <div style={{ padding: "2.5rem 1rem", textAlign: "center", color: "var(--clr-text4)", fontSize: "0.85rem" }}>{t("prices.noSeriesData")}</div>
            ) : (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 380}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--clr-border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--clr-text3)" }} minTickGap={28} />
                  <YAxis tick={{ fontSize: 11, fill: "var(--clr-text3)" }} width={56}
                         tickFormatter={(v: number) => `${v}€`} domain={["auto", "auto"]} />
                  <Tooltip
                    formatter={(v: number, key: string) => {
                      const name = key === "__lowest" ? t("prices.lowestLine") : nameOf(key);
                      return [`${Number(v).toFixed(2)} €`, name.length > 46 ? name.slice(0, 46) + "…" : name];
                    }}
                    contentStyle={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 10, fontSize: "0.75rem" }}
                  />
                  <Legend formatter={(key: string) => {
                    const p = data.presentaciones.find(x => x.cn_code === key);
                    const name = key === "__lowest" ? t("prices.lowestLine") : `${p?.is_own ? "★ " : ""}${nameOf(key)}`;
                    return <span style={{ fontSize: "0.7rem" }}>{name.length > 38 ? name.slice(0, 38) + "…" : name}</span>;
                  }} />
                  {showLowest && (
                    <Line type="stepAfter" dataKey="__lowest" stroke="#64748b" strokeWidth={1.4} strokeDasharray="5 4"
                          dot={false} connectNulls name="__lowest" />
                  )}
                  {series.map(p => (
                    <Line key={p.cn_code} type="stepAfter" dataKey={p.cn_code}
                          stroke={colorOf.get(p.cn_code)} strokeWidth={p.is_own ? 3.2 : 1.6}
                          dot={false} connectNulls name={p.cn_code} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ ...card, overflow: "hidden" }}>
            <div style={{ padding: "0.9rem 1.1rem 0.6rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>{t("prices.presentationsLabel")}</span>
              <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                {t("prices.toggleHint")}{data.omitted > 0 && t("prices.omittedProducts", { n: data.omitted })}
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--clr-border2)", textAlign: "left", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)" }}>
                    <th style={{ padding: "0.7rem 10px" }}></th>
                    <th style={{ padding: "0.7rem 10px" }}>{t("prices.colProduct")}</th>
                    {!isMobile && <th style={{ padding: "0.7rem 10px" }}>{t("prices.colLaboratory")}</th>}
                    <th style={{ padding: "0.7rem 10px", textAlign: "right" }}>{t("prices.colLatest")}</th>
                    {!isMobile && <th style={{ padding: "0.7rem 10px" }}>{t("prices.colDate")}</th>}
                    <th style={{ padding: "0.7rem 10px", textAlign: "right" }}>{t("prices.colDiff")}</th>
                    <th style={{ padding: "0.7rem 10px", textAlign: "center" }}>{t("prices.colMonths")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.presentaciones.map(p => (
                    <tr key={p.cn_code} style={{ borderBottom: "1px solid var(--clr-border2)", background: p.is_own ? `${G}0d` : undefined, fontWeight: p.is_own ? 600 : 400, opacity: p.en_agrupacion || data.sin_agrupacion ? 1 : 0.6 }}>
                      <td style={{ padding: "6px 10px" }}>
                        <input type="checkbox" checked={visible.has(p.cn_code)} disabled={!p.points}
                               onChange={() => toggleVisible(p.cn_code)} aria-label={p.product_name}
                               style={{ width: 16, height: 16, accentColor: colorOf.get(p.cn_code) }} />
                      </td>
                      <td style={{ padding: "6px 10px" }}>
                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: colorOf.get(p.cn_code), marginRight: 7, opacity: visible.has(p.cn_code) ? 1 : 0.25 }} />
                        {p.product_name}
                        <span style={{ marginLeft: 6, fontSize: "0.66rem", color: "var(--clr-text4)" }}>CN {p.cn_code}</span>
                        {p.is_own && <span style={{ marginLeft: 6, fontSize: "0.62rem", fontWeight: 700, color: G }}>· {t("prices.ownTag")}</span>}
                        {p.is_tracked && <span title={t("prices.trackedHint")} style={{ marginLeft: 6, fontSize: "0.62rem", fontWeight: 600, color: "#b45309" }}>· {t("prices.tracked")}</span>}
                        {!p.en_agrupacion && !data.sin_agrupacion && <span style={{ marginLeft: 6, fontSize: "0.62rem", color: "var(--clr-text4)" }}>· {t("prices.leftGroup")}</span>}
                      </td>
                      {!isMobile && <td style={{ padding: "6px 10px", color: "var(--clr-text3)" }}>{p.laboratory || "—"}</td>}
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>{fmt(p.latest)}</td>
                      {!isMobile && <td style={{ padding: "6px 10px", color: "var(--clr-text3)" }}>{p.latest_month || "—"}</td>}
                      <td style={{ padding: "6px 10px", textAlign: "right" }}>
                        {p.cn_code === data.ref_cn ? <span style={{ color: "var(--clr-text4)" }}>★</span> : <Diff v={p.diff_pct_vs_own} />}
                      </td>
                      <td style={{ padding: "6px 10px", textAlign: "center", color: "var(--clr-text3)" }}>{p.points}</td>
                    </tr>
                  ))}
                  {data.presentaciones.length <= 1 && (
                    <tr><td colSpan={7} style={{ padding: "1.5rem", textAlign: "center", color: "var(--clr-text4)" }}>{t("prices.noCompetitors")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0.6rem 1.1rem", fontSize: "0.7rem", color: "var(--clr-text4)" }}>{t("prices.tableFooter")}</div>
          </div>
        </>
      )}
    </div>
  );
}
